import React, { createContext, useContext, useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { guardarEnCuenta } from '../utils/preferencias';

/** Lo que elige la persona: claro, oscuro o lo que diga el sistema. */
export type Tema = 'claro' | 'oscuro' | 'sistema';

interface ThemeContextType {
  /** Lo elegido en Configuración → Perfil → Apariencia. */
  tema: Tema;
  /** Se guarda también en la cuenta, salvo con enCuenta false (al aplicar lo que viene de ella). */
  setTema: (tema: Tema, enCuenta?: boolean) => void;
  /** Lo que se ve ahora mismo: con «sistema», depende del iPhone o del ordenador. */
  darkMode: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/** Color del fondo de la app (piedra 50 y 900 de tailwind.config.js): barra del navegador. */
const COLOR_FONDO = { claro: '#F4F2EE', oscuro: '#171B1F' };

const leerTema = (): Tema => {
  try {
    const tema = localStorage.getItem('tema');
    if (tema === 'claro' || tema === 'oscuro' || tema === 'sistema') return tema;
    // La clave antigua «darkMode» se guardaba siempre (también 'false'), así que
    // de ella solo se respeta haber elegido el oscuro.
    return localStorage.getItem('darkMode') === 'true' ? 'oscuro' : 'sistema';
  } catch {
    return 'sistema';
  }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tema, setTemaEstado] = useState<Tema>(leerTema);
  const [delSistema, setDelSistema] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const darkMode = tema === 'sistema' ? delSistema : tema === 'oscuro';

  useEffect(() => {
    const consulta = window.matchMedia('(prefers-color-scheme: dark)');
    const cambiar = (e: MediaQueryListEvent) => setDelSistema(e.matches);
    consulta.addEventListener('change', cambiar);
    return () => consulta.removeEventListener('change', cambiar);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', darkMode);
    // Los controles del navegador (selector de fecha, barras de desplazamiento) a juego
    root.style.colorScheme = darkMode ? 'dark' : 'light';
    document.querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', darkMode ? COLOR_FONDO.oscuro : COLOR_FONDO.claro);
    // En la app de iOS la barra de estado (hora, batería) sigue al sistema: si la
    // app está en claro con el iPhone en oscuro, saldría texto blanco sobre blanco.
    // Style.Dark = texto claro, para fondos oscuros.
    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: darkMode ? Style.Dark : Style.Light }).catch(() => {});
    }
  }, [darkMode]);

  const setTema = (nuevo: Tema, enCuenta = true) => {
    try { localStorage.setItem('tema', nuevo); } catch { /* modo privado: solo esta sesión */ }
    setTemaEstado(nuevo);
    if (enCuenta) guardarEnCuenta({ tema: nuevo });
  };

  return (
    <ThemeContext.Provider value={{ tema, setTema, darkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme debe ser usado dentro de ThemeProvider');
  return context;
};
